import React, {useEffect, useMemo, useState} from "react";

import {ChangeDailyItemData, PartialOrder} from "@apps/daily-menu/interfaces";
import classNames from "classnames";
import moment from "moment";

import {EventOrigin, recordConversionEvent} from "@globals/config/eventUtils";
import useCartTimer from "@globals/hooks/useCartTimer";
import {CheckoutResponse} from "@globals/types/CheckoutResponse";
import {DateString} from "@globals/types/utilityTypes";
import {useUser} from "@globals/user";
import {getOrdersItems} from "@globals/utils/conversionEventHelpers";

import Button, {ButtonAccent} from "@components/Button";
import CartTimer from "@components/CartTimer";
import LoadingDots from "@components/LoadingDots";
import NomiconIcon from "@components/NomiconIcon";
import TransparentButton from "@components/TransparentButton";
import {ConfirmationModal, Modal} from "@components/modals";

import styles from "./DailyCart.scss";
import DailyCartItem from "./DailyCartItem";
import {expireOrder} from "./DailyCartService";
import MenuSurvey from "./MenuSurvey/MenuSurvey";
import {getOrderItemCount} from "./utils/getOrderItemCount";

export type DailyCartProps = {
  partialOrders?: PartialOrder[];
  handleItemUpdate: (itemData: ChangeDailyItemData) => Promise<PartialOrder>;
  checkout: () => Promise<CheckoutResponse>;
  // If we pass this prop to the component, after the checkout, it will be redirected to the next menu date. If not, no redirection.
  goToNextMenuDate?: () => void;
  selectedDate?: DateString;
  nextMenuDate?: DateString;
  isLoading?: boolean;
  resetOrder: () => void;
  onCheckoutSuccess?: (value: CheckoutResponse) => void;
};

export default function DailyCart({
  partialOrders,
  isLoading,
  resetOrder,
  handleItemUpdate,
  goToNextMenuDate,
  selectedDate,
  nextMenuDate,
  checkout,
  onCheckoutSuccess,
}: DailyCartProps): JSX.Element {
  const [showCartContent, setShowCartContent] = useState(false);
  const [showCartSuccess, setShowCartSuccess] = useState(false);
  const [surveyDate, setSurveyDate] = useState<DateString | null>(null);
  const [isCartExpirationModalOpen, setIsCartExpirationModalOpen] =
    useState(false);
  const {user} = useUser();
  const nextDayButtonText = `See ${moment(nextMenuDate).format("dddd")}'s Menu`;

  const onCartExpirationModalClose = () => {
    setIsCartExpirationModalOpen(false);

    partialOrders?.forEach((item) => expireOrder(item.id));
  };

  const secondsToExpiry = useMemo(() => {
    if (partialOrders?.length) {
      const secondsArr = partialOrders.map((order) => order.seconds_to_go);

      return Math.min(...secondsArr);
    }
  }, [partialOrders]);

  const onTimerExpire = () => {
    resetOrder();
    setIsCartExpirationModalOpen(true);
  };

  const {secondsLeft} = useCartTimer({
    secondsToCartExpiry: secondsToExpiry || 0,
    onTimerExpire,
  });

  const findLocationById = (locationId: number) => {
    const location = user?.locations_list.find(
      (location) => location.id === locationId
    );
    return location ? location.name || location.address : "";
  };

  const totalItems = getOrderItemCount(partialOrders);

  // auto open/close;
  useEffect(() => {
    const newValue = isLoading || totalItems > 0;
    if (newValue !== showCartContent) {
      setShowCartContent(newValue);
    }
  }, [totalItems, isLoading]);

  const onCheckout = () => {
    void checkout().then((checkoutResponse) => {
      // checkout request will fail if user needs to pay for their order;
      // and then redirect to the checkout page will happen;
      // if checkout request succeeds, that means user has full subsidy, conversion event value is 0;
      recordConversionEvent(EventOrigin.CART, "purchase", 0, {
        transaction_id: partialOrders?.[0].id.toString(),
        items: getOrdersItems(partialOrders),
      });

      goToNextMenuDate?.();
      onCheckoutSuccess?.(checkoutResponse);

      if (checkoutResponse.show_menu_survey) {
        setSurveyDate(partialOrders?.[0].delivery_date as DateString);
      } else {
        setShowCartSuccess(true);
      }
    });
  };

  useEffect(() => {
    if (showCartSuccess) {
      setTimeout(() => {
        setShowCartSuccess(false);
      }, 4000);
    }
  }, [showCartSuccess]);

  return (
    <div className={styles.dailyCartWrapper}>
      <TransparentButton
        onClick={() => setShowCartContent(!showCartContent)}
        aria-label={"Toggle cart"}
      >
        <div className={styles.cartIconsWrapper}>
          {totalItems > 0 && (
            <div className={styles.cartIconCounter}>{totalItems}</div>
          )}
          <NomiconIcon className={styles.cartIcon} icon="nomicon-cart" />
        </div>
      </TransparentButton>
      {showCartContent && partialOrders && partialOrders?.length > 0 && (
        <div className={styles.cartContent}>
          <div className={styles.cartHeader}>
            {isLoading ? (
              <strong className={styles.updateMessage}>Updating Cart</strong>
            ) : (
              secondsLeft && (
                <CartTimer
                  secondsLeft={secondsLeft}
                  containerClassName={styles.timer}
                  timeClassName={styles.time}
                />
              )
            )}

            <TransparentButton
              aria-label="Hide cart"
              className={styles.hideCartButton}
              onClick={() => setShowCartContent(false)}
            >
              hide –
            </TransparentButton>
          </div>
          {partialOrders?.map((partialOrder) => (
            <div key={`partial-order-${partialOrder.id}`}>
              <h4>
                {moment(partialOrder.delivery_date, `YYYY-MM-DD`).format(
                  `dddd`
                )}{" "}
                ({findLocationById(partialOrder.location_id)})
              </h4>
              <div className={styles.cartItems}>
                {partialOrder.order_items.map((orderItem) => (
                  <DailyCartItem
                    locationId={partialOrder.location_id}
                    key={`cart-item-${orderItem.id}`}
                    orderItem={orderItem}
                    handleItemUpdate={handleItemUpdate}
                    orderId={partialOrder.id}
                  />
                ))}
              </div>
            </div>
          ))}
          <div className={styles.actionButtons}>
            {nextMenuDate && (
              <Button
                accent={ButtonAccent.AccentOrange}
                withThickBorder
                aria-label={nextDayButtonText}
                onClick={goToNextMenuDate}
              >
                {nextDayButtonText}
              </Button>
            )}
            <Button
              accent={ButtonAccent.AccentOrange}
              aria-label="checkout"
              withThickBorder
              onClick={onCheckout}
              disabled={isLoading}
            >
              {isLoading ? <LoadingDots /> : "Checkout"}
            </Button>
          </div>
        </div>
      )}
      <div
        data-testid="checkout-success"
        className={classNames(
          styles.cartSuccess,
          styles.successPointer,
          showCartSuccess && styles.visible
        )}
      >
        <p>Your order has been confirmed.</p>
        {nextMenuDate && (
          <p>
            Here's the menu for{" "}
            {moment(selectedDate, "YYYY-MM-DD").format("dddd")}!
          </p>
        )}
      </div>
      <MenuSurvey
        className={classNames(styles.cartSuccess, styles.visible)}
        closeSurvey={() => setSurveyDate(null)}
        surveyDate={surveyDate}
      />
      <Modal
        isOpen={isCartExpirationModalOpen}
        onClose={onCartExpirationModalClose}
        title="Sorry, Time's Up!"
        confirmButtonText="Back to menu"
        confirmationContent="Sorry, the time has run out in your cart. Please add items again."
        cancelButtonText=""
        modalComponent={ConfirmationModal}
      ></Modal>
    </div>
  );
}
